import { WorkspaceSubscriptionPlan } from '@airimpact/types/modelEnums'
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace'
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Chip, Dialog, IconButton, Typography } from '@mui/material'
import { Elements } from '@stripe/react-stripe-js'
import { Stripe, StripeElements } from '@stripe/stripe-js'
import moment from 'moment'
import React, { Dispatch, SetStateAction, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { CheckoutForm } from './CheckoutForm'
import NumberOfEntityBox from './NumberOfEntityBox'
import { TierSelectionSummary } from './TierSelectionSummary'
import { useProjectsContext } from '../../hooks/useProjects'
import { useSnackbarContext } from '../../hooks/useSnackBar'
import { useStripePromise } from '../../hooks/useStripePromise'
import {
  ICustomerProfileResponse,
  ISubscribePayload,
  ISubscribeUpdatePayload,
  ITaxDetailsPayload,
  PlanData,
  useTierSelectionContext,
} from '../../hooks/useTierSelection'
import { useWorkspaceContext } from '../../hooks/useWorkspace'
import { ReactComponent as ArrowRight } from '../../icons/arrow-right-checkout.svg'
import { ReactComponent as CheckIcon } from '../../icons/Check icon.svg'
import { ReactComponent as CheckboxBase } from '../../icons/checkbox-base.svg'
import { ReactComponent as CloseIcon } from '../../icons/close-icon.svg'
import { pluralize } from '../../utils/pluralize'
import { polling } from '../../utils/polling'

export interface IError {
  response: {
    data: {
      message: string
    }
  }
}

export type StripeStateType = {
  stripe: Stripe
  elements: StripeElements
}

enum CheckoutStep {
  default = 'default',
  paymentAndBilling = 'paymentAndBilling',
}

interface PaymentMethodState {
  hasError: boolean
  isValid: boolean
}

export function CheckoutScreen({
  selectedPlan,
  checked,
  isTierView,
  setChecked,
  isManageUsageScreen,
  setIsManageUsageScreen,
  onClose,
  setIsCheckoutScreen,
  setIsTierSelectionPopUpOpen,
}: {
  selectedPlan: PlanData
  checked: boolean
  isTierView?: boolean
  setChecked: (value: boolean) => void
  isManageUsageScreen?: boolean
  setIsManageUsageScreen?: Dispatch<SetStateAction<boolean>>
  onClose: () => void
  setIsCheckoutScreen: (isCheckoutScreen: boolean) => void
  setIsTierSelectionPopUpOpen?: (isTierSelectionPopUpOpen: boolean) => void
}) {
  const { selectedWorkspaceDetails, updateSelectedWorkspace, selectedNodeId } = useWorkspaceContext()
  const {
    subscribe,
    editSubscription,
    customerProfile,
    updateCustomerProfile,
    currentPlanDetails,
    subscriptionAddons,
    stripeKey,
    currentPlanName,
    addNewPaymentMethod,
    getCustomerProfile,
    subscriptionPlansDetails,
    addTaxDetails,
    updateTaxDetails,
    deleteTaxDetails,
  } = useTierSelectionContext()
  const { openSnackBar } = useSnackbarContext()
  const { fetchProjects } = useProjectsContext()

  const featureUser = selectedWorkspaceDetails.subscription?.features.find(
    (feature) => feature.scope === 'WORKSPACE_USER',
  )
  const featureProjects = selectedWorkspaceDetails.subscription?.features.find(
    (feature) => feature.scope === 'WORKSPACE_PROJECTS',
  )

  const maximumNumberOfProjects = featureProjects?.count?.max || 0
  const availableNumberOfUsers = (featureUser?.count?.max || 0) - (featureUser?.count?.current || 0)
  const maximumNumberOfUsers = isTierView ? 1 : featureUser?.count?.max || 1
  const defaultNumberOfProjects =
    subscriptionPlansDetails.find((planDetails) => planDetails.plan === selectedPlan.plan)?.limits.projects || 0

  const [isCardComplete, setIsCardComplete] = useState<boolean>(false)
  const [isAddressComplete, setIsAddressComplete] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isPreviewLoading, setIsPreviewLoading] = useState<boolean>(false)
  // const [isSkipPaymentFlow, setIsSkipPaymentFlow] = useState<boolean>(false)
  const [stripeState, setStripeState] = useState<StripeStateType | null>(null)
  const [numberOfUsers, setNumberOfUsers] = useState<number>(maximumNumberOfUsers)
  const [numberOfProjects, setNumberOfProjects] = useState<number>(
    selectedPlan.plan !== currentPlanName
      ? defaultNumberOfProjects
      : maximumNumberOfProjects !== 0
        ? maximumNumberOfProjects
        : defaultNumberOfProjects,
  )
  const [clientSecret, setClientSecret] = React.useState<string>('')
  const [isAmountExceeded, setIsAmountExceeded] = useState<boolean>(false)
  const [checkoutStep, setCheckoutStep] = useState<CheckoutStep>(CheckoutStep.default)
  const [isSubscriptionUpgrading, setIsSubscriptionUpgrading] = useState<boolean | undefined>(undefined)
  const [stripeKeyForCanceledSubscription, setStripeKeyForCanceledSubscription] = useState<string>('')
  const [taxDetails, setTaxDetails] = useState<ITaxDetailsPayload>({
    type: customerProfile?.provider?.tax_ids?.[0]?.type ?? '',
    value: customerProfile?.provider?.tax_ids?.[0]?.value ?? '',
  })
  const [invalidTaxDetails, setInvalidTaxDetails] = useState<boolean>(false)
  const paymentMethodState = useRef<PaymentMethodState>({
    hasError: false,
    isValid: false,
  })

  const navigate = useNavigate()

  const selectedInterval = checked
    ? selectedPlan.intervals.find((intervalDetails) => intervalDetails.interval === 'year')
    : selectedPlan.intervals.find((intervalDetails) => intervalDetails.interval === 'month')
  const isEligibleForTrial = selectedWorkspaceDetails.subscription?.isEligibleForTrial
  const hasSubscription = selectedWorkspaceDetails?.subscription?.data
  const isCanceledSubscription = Boolean(isTierView && !isEligibleForTrial && !hasSubscription)
  const isTrial =
    !currentPlanDetails ||
    !hasSubscription ||
    currentPlanDetails?.data.data.transaction_status === 'trialing' ||
    isEligibleForTrial
  const hasDefaultPaymentMethod = Boolean(customerProfile?.provider.payment_methods.find((method) => method.default))
  const hasValidNumberOfUsers = numberOfUsers >= 1
  const hasValidNumberOfProjects = numberOfProjects >= defaultNumberOfProjects
  const addonSeat = subscriptionAddons.find((addon) => addon.plan === selectedPlan.plan && addon.key === 'Seat')
  const addonProject = subscriptionAddons.find((addon) => addon.key === 'Project')
  const amountOfAdditionalSeats = numberOfUsers === 1 ? 0 : numberOfUsers - 1
  const amountOfAdditionalProjects =
    numberOfProjects === defaultNumberOfProjects ? 0 : numberOfProjects - defaultNumberOfProjects

  const stripeKeyToUse =
    stripeKeyForCanceledSubscription !== '' && isCanceledSubscription ? stripeKeyForCanceledSubscription : stripeKey
  const { stripePromise } = useStripePromise(stripeKeyToUse)

  const currentDate = moment()
  const renewDate = !isTrial
    ? checked
      ? currentDate.add(1, 'years')
      : currentDate.add(1, 'months')
    : currentDate.add(1, 'months')
  const formattedRenewDate = renewDate.format('MMMM D, YYYY')
  // TODO reuse end date
  const endDate = isTrial ? currentPlanDetails?.provider?.trial.end : currentPlanDetails?.provider?.period.end
  const currentInterval = currentPlanDetails?.provider?.plan.interval

  const onPaymentSuccess = async () => {
    setClientSecret('')
    openSnackBar({
      message: 'Subscription succeeded!',
      type: 'success',
    })
    onClose()
    setIsLoading(false)
    setTimeout(() => {
      updateSelectedWorkspace()
    }, 1000)
    setTimeout(() => {
      updateSelectedWorkspace()
    }, 1000)
  }

  const onAddNewPaymentMethod = async () => {
    try {
      const response = await addNewPaymentMethod()
      setClientSecret(response.client_secret)
      setStripeKeyForCanceledSubscription(response.stripe_key)
      await getCustomerProfile()
    } catch (error) {
      console.log(error)
    }
  }

  const displayPaymentStepInCheckout = async () => {
    await onAddNewPaymentMethod()
    setCheckoutStep(CheckoutStep.paymentAndBilling)
  }

  const appearance = {
    theme: 'stripe',
  }
  const options = {
    appearance,
    clientSecret,
  }

  const handleUsersIncrement = () => {
    setNumberOfUsers((prevValue) => prevValue + 1)
  }

  const handleUsersDecrement = () => {
    setNumberOfUsers((prevValue) => prevValue - 1)
  }

  const handleProjectsIncrement = () => {
    setNumberOfProjects((prevValue) => prevValue + 1)
  }

  const handleProjectsDecrement = () => {
    setNumberOfProjects((prevValue) => prevValue - 1)
  }

  const handleSubmit = async () => {
    if (!stripeState) {
      console.log('Stripe has not loaded yet.')
      return
    }

    const { stripe, elements } = stripeState
    await elements.submit()
    setIsLoading(true)

    const { error } = await stripe.confirmSetup({
      elements,
      clientSecret,

      redirect: 'if_required',
    })

    let errorMessage = ''
    if (!error) {
      if (!customerProfile?.provider.profile.address) {
        const addressElement = stripeState.elements.getElement('address')
        if (!addressElement) {
          console.error('Address element not found')
          setIsLoading(false)
          return
        }
        const { complete, value } = await addressElement.getValue()

        let payload

        if (customerProfile) {
          payload = {
            name: value.name,
            phone: value.phone === '' || !value.phone ? null : value.phone,
            address: value.address,
          }
        }

        const taxId = customerProfile?.provider?.tax_ids?.[0]

        if (taxId && taxDetails.value === '') {
          await deleteTaxDetails(taxId.id)
        } else if (taxDetails.value !== '' && (!taxId || taxId?.value !== taxDetails.value)) {
          if (taxId) {
            await updateTaxDetails(taxId?.id, taxDetails)
          } else {
            await addTaxDetails(taxDetails)
          }
        }

        if (complete && payload) {
          await updateCustomerProfile(payload)
        }
        paymentMethodState.current.hasError = false
      }
      setTimeout(() => {
        openSnackBar({
          message: 'Subscription succeeded!',
          type: 'success',
        })
        onClose()
        if (isTierView) {
          navigate(`/${selectedNodeId}`)
        }
        setIsLoading(false)
        updateSelectedWorkspace()
      }, 2000)

      if (!isTrial) {
        const hasValidPaymentMethod = await polling<ICustomerProfileResponse>({
          fn: async () => await getCustomerProfile(),
          validate: (profile: ICustomerProfileResponse) => {
            const hasPaymentMethods = profile.provider?.payment_methods.length > 0
            const hasDefaultMethod = profile.provider?.payment_methods.some((payment_method) => payment_method.default)

            return Boolean(hasPaymentMethods && hasDefaultMethod)
          },
          interval: 500,
          maxAttempts: 5,
        }).catch(() => false)

        if (!hasValidPaymentMethod) {
          openSnackBar({
            message: 'Something is wrong with a payment method, please re-try with a valid one',
            type: 'error',
          })
          setIsLoading(false)
          paymentMethodState.current.isValid = false
          return
        }
      }
      paymentMethodState.current.isValid = true
      paymentMethodState.current.hasError = false
      return
    }
    if (error?.type === 'card_error' || error?.type === 'validation_error') {
      errorMessage = error.message || ''
    } else {
      errorMessage = 'An unexpected error occurred.'
    }
    openSnackBar({
      message: errorMessage,
      type: 'error',
    })
    paymentMethodState.current.hasError = true
    setIsLoading(false)
  }

  const onSubscribe = async () => {
    try {
      if (!selectedPlan) {
        return
      }
      setIsLoading(true)

      const interval = selectedInterval?.interval || ''
      const items = [
        {
          product_id: selectedPlan._id || '',
          quantity: 1,
        },
        ...(amountOfAdditionalSeats > 0
          ? [
              {
                product_id: addonSeat?._id || '',
                quantity: amountOfAdditionalSeats,
              },
            ]
          : []),
        ...(amountOfAdditionalProjects > 0
          ? [
              {
                product_id: addonProject?._id || '',
                quantity: amountOfAdditionalProjects,
              },
            ]
          : []),
      ]

      let payload: ISubscribePayload | ISubscribeUpdatePayload

      if (hasSubscription) {
        payload = {
          immediate:
            isTrial || currentPlanName === WorkspaceSubscriptionPlan.Free
              ? true
              : currentInterval !== selectedInterval?.interval
                ? false
                : Boolean(isSubscriptionUpgrading),
          interval,
          items,
        } as ISubscribeUpdatePayload
      } else {
        payload = {
          interval,
          items,
        } as ISubscribePayload
      }

      if (hasSubscription) {
        await editSubscription(payload as ISubscribeUpdatePayload)
        setIsSubscriptionUpgrading(undefined)
      } else {
        await subscribe(payload as ISubscribePayload)
      }

      await updateSelectedWorkspace()
      await fetchProjects()

      openSnackBar({
        message: hasSubscription ? 'Subscription updated!' : 'Subscription created!',
        type: 'success',
      })

      if (isTierView) {
        navigate(`/${selectedNodeId}`)
      }

      setIsLoading(false)
      onClose()
    } catch (e) {
      console.error(e)
      const errorMessage = (e as IError)?.response?.data?.message || 'Something went wrong!'
      openSnackBar({
        message: errorMessage,
        type: 'error',
      })
      setIsLoading(false)
    }
  }

  const handleTaxDetailsChange = (type: string, value: string) => {
    setTaxDetails((prevTaxDetails) => ({
      ...prevTaxDetails,
      [type]: value,
    }))
  }

  return (
    <Dialog
      open
      onClose={() => {
        if (!isLoading) {
          onClose()
          setIsSubscriptionUpgrading(undefined)
        }
      }}
      maxWidth="xl"
      sx={{
        '& .MuiPaper-root': {
          maxWidth: '1350px',
          borderRadius: '12px',
        },
      }}
    >
      <Box
        sx={{
          position: 'relative',
          boxShadow: '0px 4px 6px -2px rgba(16, 24, 40, 0.03), 0px 12px 16px -4px rgba(16, 24, 40, 0.08)',
          padding: '24px 48px 48px',
        }}
      >
        <IconButton
          onClick={() => {
            if (!isLoading) {
              onClose()
              setIsSubscriptionUpgrading(undefined)
            }
          }}
          sx={{ position: 'absolute', zIndex: 10, top: '22px', right: '22px', marginLeft: 'auto', padding: 0 }}
        >
          <CloseIcon />
        </IconButton>
        <Box display="flex" width="fit-content" flexDirection="column">
          <Button
            variant="text"
            focusRipple={false}
            startIcon={<KeyboardBackspaceIcon />}
            sx={{ padding: 0, margin: 0, mr: 'auto', mb: 5 }}
            onClick={() => {
              if (!isLoading) {
                if (setIsTierSelectionPopUpOpen) {
                  setIsTierSelectionPopUpOpen(true)
                }
                setIsCheckoutScreen(false)
                if (setIsManageUsageScreen) {
                  setIsManageUsageScreen(false)
                }
              }
            }}
          >
            {isManageUsageScreen ? 'Manage subscription plan' : 'Choose another plan'}
          </Button>
          {clientSecret && (
            <Typography variant="display5" color="primary.800" mb={3}>
              Checkout
            </Typography>
          )}

          {hasSubscription && (
            <Box sx={{ marginBottom: 4 }}>
              {!isManageUsageScreen ? (
                <Typography variant="h5" color="primary.600">
                  {selectedPlan.plan === WorkspaceSubscriptionPlan.Free && 'Upgrade to'}
                  {selectedPlan.plan === WorkspaceSubscriptionPlan.Essentials &&
                    (currentPlanName === WorkspaceSubscriptionPlan.Advanced ||
                      currentPlanName === WorkspaceSubscriptionPlan.Premium) &&
                    'Downgrade to'}
                  {selectedPlan.plan === WorkspaceSubscriptionPlan.Advanced &&
                    currentPlanName === WorkspaceSubscriptionPlan.Premium &&
                    'Downgrade to'}
                  {selectedPlan.plan === WorkspaceSubscriptionPlan.Advanced &&
                    currentPlanName === WorkspaceSubscriptionPlan.Essentials &&
                    'Upgrade to'}
                  {selectedPlan.plan === WorkspaceSubscriptionPlan.Premium &&
                    (currentPlanName === WorkspaceSubscriptionPlan.Essentials ||
                      currentPlanName === WorkspaceSubscriptionPlan.Advanced) &&
                    'Upgrade to'}
                </Typography>
              ) : (
                <Typography variant="h5" color="primary.600">
                  Manage usage
                </Typography>
              )}
              <Typography variant="display5" color="primary.800" mb={3.5}>
                {selectedPlan.plan} plan
              </Typography>
            </Box>
          )}

          {isCanceledSubscription && (
            <Typography variant="display5" color="primary.800" mb={3.5}>
              {selectedPlan.plan} plan
            </Typography>
          )}

          {!isCanceledSubscription && !isManageUsageScreen && !hasSubscription && (
            <>
              <Typography variant="subtitle1" color="primary.800">
                {`Try ${selectedPlan.plan} plan`}
              </Typography>
              <Typography variant="h3" color="primary.800" mb={2}>
                30 Days Free Trial
              </Typography>
            </>
          )}

          {/* {!clientSecret && isTrial && (
        <Box display="flex" alignItems="center" mb={2}>
          <IOSSwitch
            checked={isSkipPaymentFlow}
            onChange={(ev, checked) => setIsSkipPaymentFlow(checked)}
            trackColor="grey"
          />
          <Typography color="grey.500" variant="caption2" ml={1}>
            Skip payment details for now
          </Typography>
        </Box>
      )} */}
          {!isCanceledSubscription && !isManageUsageScreen && isTrial && (
            <Typography variant="caption" sx={{ color: 'grey.700', mb: 1.5 }}>
              After the trial expires
            </Typography>
          )}

          <Box display="flex" justifyContent="space-between">
            {checkoutStep === CheckoutStep.paymentAndBilling && stripePromise ? (
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              <Elements options={options} stripe={stripePromise}>
                <CheckoutForm
                  onPaymentSuccess={onPaymentSuccess}
                  clientSecret={clientSecret}
                  onStripeLoaded={setStripeState}
                  showAddressForm={!customerProfile?.provider.profile.address}
                  setIsAddressComplete={setIsAddressComplete}
                  setIsCardComplete={setIsCardComplete}
                  showTaxForm={customerProfile?.provider.tax_ids && customerProfile?.provider.tax_ids.length === 0}
                  taxDetails={taxDetails}
                  handleTaxDetailsChange={handleTaxDetailsChange}
                  setInvalidTaxDetails={setInvalidTaxDetails}
                />
              </Elements>
            ) : (
              <Box display="flex" flexDirection="column">
                {!isManageUsageScreen && (
                  <Box display="flex" mb={6}>
                    <Box
                      width="260px"
                      borderRadius="8px"
                      border="1px solid"
                      borderColor={checked ? 'secondary.400' : 'grey.200'}
                      onClick={() => setChecked(true)}
                      sx={{
                        cursor: 'pointer',
                      }}
                    >
                      <Box
                        p={2}
                        borderRadius="8px 8px 0px 0px"
                        bgcolor={checked ? 'graygreen.25' : 'white'}
                        borderBottom="1px solid"
                        borderColor={checked ? 'secondary.400' : 'grey.200'}
                        display="flex"
                        alignItems="center"
                        height="56px"
                        boxSizing="border-box"
                      >
                        {checked ? (
                          <CheckIcon
                            style={{
                              height: '16px',
                              width: '16px',
                            }}
                          />
                        ) : (
                          <CheckboxBase
                            style={{
                              height: '16px',
                              width: '16px',
                            }}
                          />
                        )}
                        <Typography color="primary.800" variant="subtitle1" fontWeight={500} ml={1}>
                          Billed Annually
                        </Typography>

                        <Chip
                          label="Save 20%"
                          sx={{
                            marginLeft: 1,
                            borderRadius: '16px',
                            backgroundColor: 'secondary.50',
                            color: 'secondary.700',
                            fontSize: '12px',
                            height: '24px',
                          }}
                        />
                      </Box>
                      <Box p={2} display="flex" alignItems="baseline" height="70px" boxSizing="border-box">
                        <Typography color="grey.700" variant="h1">
                          <b>
                            $
                            {(Number(
                              selectedPlan.intervals.find((intervalDetails) => intervalDetails.interval === 'year')
                                ?.unit_amount_decimal,
                            ) || 0) /
                              100 /
                              12}
                          </b>
                        </Typography>
                        <Typography color="grey.500" variant="body2" ml={1} mb={0.5}>
                          USD / user / month
                        </Typography>
                      </Box>
                    </Box>
                    <Box
                      borderRadius="8px"
                      border="1px solid"
                      borderColor={!checked ? 'secondary.400' : 'grey.200'}
                      onClick={() => setChecked(false)}
                      sx={{
                        cursor: 'pointer',
                      }}
                      ml={2}
                      width="260px"
                    >
                      <Box
                        p={2}
                        borderRadius="8px 8px 0px 0px"
                        bgcolor={!checked ? 'graygreen.25' : 'white'}
                        borderBottom="1px solid"
                        borderColor={!checked ? 'secondary.400' : 'grey.200'}
                        display="flex"
                        alignItems="center"
                        height="56px"
                        boxSizing="border-box"
                      >
                        {!checked ? (
                          <CheckIcon
                            style={{
                              height: '16px',
                              width: '16px',
                            }}
                          />
                        ) : (
                          <CheckboxBase
                            style={{
                              height: '16px',
                              width: '16px',
                            }}
                          />
                        )}
                        <Typography color="primary.800" variant="subtitle1" fontWeight={500} ml={1}>
                          Billed Monthly
                        </Typography>
                      </Box>
                      <Box p={2} display="flex" alignItems="baseline" height="70px" boxSizing="border-box">
                        <Typography color="grey.700" variant="h1">
                          <b>
                            $
                            {(Number(
                              selectedPlan.intervals.find((intervalDetails) => intervalDetails.interval === 'month')
                                ?.unit_amount_decimal,
                            ) || 0) / 100}
                          </b>
                        </Typography>
                        <Typography color="grey.500" variant="body2" ml={1} mb={0.5}>
                          USD / user / month
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                )}

                <NumberOfEntityBox
                  title="Number of users"
                  description={
                    isManageUsageScreen ? (
                      <>
                        <span>{availableNumberOfUsers}</span> seat(s) available out of{' '}
                        <span>{maximumNumberOfUsers}</span> purchased. Increase to add additional seats.
                      </>
                    ) : (
                      <>How many seats do you need?</>
                    )
                  }
                  isEditable
                  value={numberOfUsers}
                  setValue={setNumberOfUsers}
                  hasValidValue={hasValidNumberOfUsers}
                  errorMessage="You need a minimum of 1 User seat."
                  handleDecrement={handleUsersDecrement}
                  handleIncrement={handleUsersIncrement}
                  hasEntityDowngrade={Boolean(numberOfUsers >= 1 && (featureUser?.count?.current || 0) > numberOfUsers)}
                  entityDowngradeMessage={
                    <Box sx={{ display: 'flex', maxWidth: '490px', marginTop: 1 }}>
                      <WarningAmberRoundedIcon sx={{ fill: '#ce9036' }} />

                      <Typography
                        variant="body2"
                        color="warning.700"
                        ml={0.75}
                        sx={{ '& span': { color: 'warning.800' } }}
                      >
                        You need to&nbsp;
                        <span>
                          deactivate&nbsp;
                          {pluralize(
                            (featureUser?.count?.current || 0) - numberOfUsers,
                            'workspace user',
                            'workspace users',
                          )}
                          &nbsp;before&nbsp;
                          {endDate ? moment.unix(endDate).utc().format('MMMM DD, YYYY') : formattedRenewDate}
                        </span>
                        &nbsp; due to your subscription change, otherwise the most recently added users will be
                        deactivated automatically
                      </Typography>
                    </Box>
                  }
                />

                {((currentPlanName === WorkspaceSubscriptionPlan.Premium && isManageUsageScreen) ||
                  !isManageUsageScreen) && (
                  <NumberOfEntityBox
                    title="Number of projects"
                    description={
                      (currentPlanName === WorkspaceSubscriptionPlan.Premium && isManageUsageScreen) ||
                      (selectedPlan.plan === WorkspaceSubscriptionPlan.Premium && !isManageUsageScreen) ? (
                        <>
                          Up to <span>{defaultNumberOfProjects} projects</span> included in your{' '}
                          <span>{selectedPlan.plan} Plan</span>, you can increase.
                        </>
                      ) : (
                        <>
                          {defaultNumberOfProjects} project{defaultNumberOfProjects > 1 ? 's' : ''} included in{' '}
                          <span>{selectedPlan.plan} Plan</span>
                        </>
                      )
                    }
                    isEditable={
                      (currentPlanName === WorkspaceSubscriptionPlan.Premium && isManageUsageScreen) ||
                      (selectedPlan.plan === WorkspaceSubscriptionPlan.Premium && !isManageUsageScreen)
                    }
                    value={numberOfProjects}
                    setValue={setNumberOfProjects}
                    hasValidValue={hasValidNumberOfProjects}
                    errorMessage={`Your ${selectedPlan.plan} plan requires at least ${defaultNumberOfProjects} projects.`}
                    handleDecrement={handleProjectsDecrement}
                    handleIncrement={handleProjectsIncrement}
                    hasEntityDowngrade={Boolean(
                      (isManageUsageScreen && (featureProjects?.count?.current || 0) > numberOfProjects) ||
                        (!isManageUsageScreen &&
                          selectedPlan.plan !== currentPlanName &&
                          (featureProjects?.count?.current || 0) > defaultNumberOfProjects),
                    )}
                    entityDowngradeMessage={
                      <Box sx={{ display: 'flex', maxWidth: '490px', marginTop: 1 }}>
                        <WarningAmberRoundedIcon sx={{ fill: '#ce9036' }} />

                        <Typography
                          variant="body2"
                          color="warning.700"
                          ml={0.75}
                          sx={{ '& span': { color: 'warning.800' } }}
                        >
                          You need to{' '}
                          <span>
                            archive&nbsp;
                            {pluralize(
                              (featureProjects?.count?.current || 0) -
                                (selectedPlan.plan !== currentPlanName ? defaultNumberOfProjects : numberOfProjects),
                              'project',
                              'projects',
                            )}
                            &nbsp; before&nbsp;
                            {endDate ? moment.unix(endDate).utc().format('MMMM DD, YYYY') : formattedRenewDate}
                          </span>
                          &nbsp; due to your subscription change, otherwise the most recently added projects will be
                          archived automatically.
                        </Typography>
                      </Box>
                    }
                  />
                )}
              </Box>
            )}

            <Box display="flex" flexDirection="column" ml={4}>
              <TierSelectionSummary
                isLoading={isPreviewLoading}
                setIsLoading={setIsPreviewLoading}
                isManageUsageScreen={isManageUsageScreen}
                planDetails={selectedPlan}
                selectedInterval={checked ? 'year' : 'month'}
                isTrial={
                  !isCanceledSubscription &&
                  (!currentPlanDetails ||
                    currentPlanDetails?.data.data.transaction_status === 'trialing' ||
                    (currentPlanDetails?.data.data.transaction_status === 'active' && isEligibleForTrial) ||
                    (!isEligibleForTrial && !currentPlanDetails))
                } // TODO to refactor the naming, can be not only a trial
                numberOfUsers={numberOfUsers}
                numberOfProjects={numberOfProjects}
                maximumNumberOfUsers={maximumNumberOfUsers}
                maximumNumberOfProjects={maximumNumberOfProjects}
                defaultNumberOfProjects={defaultNumberOfProjects}
                currentPlanProvider={currentPlanDetails?.provider}
                onAmountExceeded={setIsAmountExceeded}
                isSubscriptionUpgrading={isSubscriptionUpgrading}
                setIsSubscriptionUpgrading={setIsSubscriptionUpgrading}
                selectedPlanId={selectedPlan._id}
                selectedAddonSeatId={addonSeat?._id || ''}
                selectedAddonProjectId={addonProject?._id || ''}
                amountOfAdditionalSeats={amountOfAdditionalSeats}
                amountOfAdditionalProjects={amountOfAdditionalProjects}
                isTierView={isTierView}
                isCanceledSubscription={isCanceledSubscription}
              />
              <LoadingButton
                variant="contained"
                onClick={async () => {
                  if (
                    (isCanceledSubscription && checkoutStep === CheckoutStep.default) ||
                    (!isTrial && !hasDefaultPaymentMethod && checkoutStep === CheckoutStep.default)
                  ) {
                    displayPaymentStepInCheckout()
                  } else if (checkoutStep === CheckoutStep.paymentAndBilling) {
                    await handleSubmit()
                    if (!paymentMethodState.current.hasError && paymentMethodState.current.isValid) {
                      await onSubscribe()
                    }
                  } else {
                    onSubscribe()
                  }
                }}
                loading={isLoading}
                disabled={
                  numberOfUsers === 0 ||
                  !hasValidNumberOfUsers ||
                  !hasValidNumberOfProjects ||
                  isAmountExceeded ||
                  isPreviewLoading ||
                  (isManageUsageScreen &&
                    (currentPlanName as WorkspaceSubscriptionPlan) !== WorkspaceSubscriptionPlan.Premium &&
                    numberOfUsers === maximumNumberOfUsers) ||
                  (isManageUsageScreen &&
                    currentPlanName === WorkspaceSubscriptionPlan.Premium &&
                    numberOfProjects === maximumNumberOfProjects &&
                    numberOfUsers === maximumNumberOfUsers) ||
                  (checkoutStep === CheckoutStep.paymentAndBilling &&
                    (invalidTaxDetails ||
                      !isCardComplete ||
                      (!customerProfile?.provider.profile.address && !isAddressComplete)))
                }
                sx={{
                  marginTop: '32px',
                }}
                endIcon={<ArrowRight />}
              >
                {(isCanceledSubscription && checkoutStep === CheckoutStep.default) ||
                (!isTrial && !hasDefaultPaymentMethod && checkoutStep === CheckoutStep.default)
                  ? 'Confirm'
                  : 'Confirm and Checkout'}
              </LoadingButton>
            </Box>
          </Box>
        </Box>
      </Box>
    </Dialog>
  )
}
